import { Sparkles, Upload, Server } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";

/**
 * AiSourceBadge
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.3
 *
 * Provenance pill rendered next to a row when the row was not authored by a
 * user. Three variants:
 *   - `AI`     — any `ai_*` source (e.g. `ai_extract`, `ai_inferred`)
 *   - `Import` — CSV/bulk import
 *   - `System` — internal automation
 *
 * Hidden automatically for `source === "user"`. Pass `hideImport` on list
 * views to suppress the post-CSV-import noise (still shows AI/System).
 *
 * All three render as `variant="outline"` with the violet rim + sparkles
 * accent so any non-user provenance is visually consistent.
 */
export interface AiSourceBadgeProps {
  /**
   * Raw provenance string from the worker. Anything starting with `ai`
   * resolves to AI, exact `import` / `csv` to Import, exact `system` /
   * `cron` / `webhook` to System. `user` (or missing) renders nothing.
   */
  source?: string | null;
  /** Hide the Import variant entirely. Useful on lists right after a CSV import. */
  hideImport?: boolean;
  className?: string;
  "data-testid"?: string;
}

type ResolvedKind = "ai" | "import" | "system" | null;

function resolveSource(source: string | null | undefined): ResolvedKind {
  if (!source) return null;
  const s = source.toLowerCase();
  if (s === "user") return null;
  if (s.startsWith("ai")) return "ai";
  if (s === "import" || s === "csv" || s === "bulk_import") return "import";
  if (
    s === "system" ||
    s === "cron" ||
    s === "webhook" ||
    s === "automation"
  )
    return "system";
  // Unknown source — render as AI by default so we never silently drop
  // provenance information.
  return "ai";
}

export function AiSourceBadge({
  source,
  hideImport = false,
  className,
  "data-testid": dataTestId,
}: AiSourceBadgeProps) {
  const kind = resolveSource(source);
  if (!kind) return null;
  if (kind === "import" && hideImport) return null;

  const label = kind === "ai" ? "AI" : kind === "import" ? "Import" : "System";
  const Icon = kind === "import" ? Upload : kind === "system" ? Server : Sparkles;

  return (
    <Badge
      variant="outline"
      data-testid={dataTestId ?? `badge-source-${kind}`}
      className={cn(
        "border-violet-500 text-violet-600 dark:text-violet-400",
        "text-[10px]",
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}
